import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

interface ObjectBall {
  pos: THREE.Vector3;
  radius: number;
  color?: number; // 0xRRGGBB of the ball (for the aim overlap tint)
}

/** How fully the cue ball would strike the first object ball along the aim line. */
export interface AimOverlap {
  /** 0..1 — share of the object ball covered: 1 = dead-centre (full ball), 0 = a grazing edge hit. */
  fraction: number;
  /** Signed lateral offset of the object-ball centre from the aim line, in cue-ball-radius
   * units (negative = object ball left of travel, positive = right). For drawing the overlap. */
  side: number;
  /** 0xRRGGBB colour of the ball being targeted, for tinting the overlap. */
  color: number;
}

/** Aim line + ghost-ball marker showing the cue ball's first predicted contact. */
export class AimVisuals {
  readonly group = new THREE.Group();
  // A plain THREE.Line renders as a raw 1px GL line with no reliable
  // antialiasing across GPUs — barely visible in the angled aim view (it
  // recedes into the distance, foreshortened), but a jagged staircase in the
  // straight-down top view, where the line can lie at any angle on screen
  // with no foreshortening to hide it. Line2 draws it as a proper
  // screen-space ribbon instead, so it stays crisp and dead straight from
  // any camera angle.
  private readonly line: Line2;
  private readonly lineMaterial: LineMaterial;
  private readonly ghost: THREE.Mesh;
  visible = true;
  /** Overlap with the first ball hit this aim, or null if the aim meets only a cushion. */
  overlap: AimOverlap | null = null;

  constructor() {
    const geo = new LineGeometry();
    geo.setPositions([0, 0, 0, 0, 0, 0]);
    this.lineMaterial = new LineMaterial({ color: 0xffffff, transparent: true, opacity: 0.7, linewidth: 1.5 });
    this.lineMaterial.resolution.set(window.innerWidth, window.innerHeight);
    this.line = new Line2(geo, this.lineMaterial);
    this.ghost = new THREE.Mesh(
      new THREE.TorusGeometry(0.03, 0.0035, 8, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 })
    );
    this.ghost.rotation.x = Math.PI / 2;
    this.group.add(this.line, this.ghost);
  }

  /** Keep the line's pixel width correct as the canvas resizes (LineMaterial
   *  measures its width against this resolution, not the render target). */
  setResolution(width: number, height: number): void {
    this.lineMaterial.resolution.set(width, height);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.group.visible = v;
  }

  /** Recompute the aim line to the first ball/cushion contact along the aim direction. */
  update(
    cueBall: THREE.Vector3,
    azimuth: number,
    objects: ObjectBall[],
    ballRadius: number,
    halfW: number,
    halfL: number
  ): void {
    if (!this.visible) {
      this.overlap = null;
      return;
    }
    const dir = new THREE.Vector3(Math.cos(azimuth), Math.sin(azimuth), 0);

    let tHit = Infinity;
    let hitCenter: THREE.Vector3 | null = null;
    let hitRadius = ballRadius;
    let hitColor = 0xffffff;

    // ray vs each object ball (expanded by the cue ball radius = collision distance)
    for (const ob of objects) {
      const t = rayCircle(cueBall, dir, ob.pos, ob.radius + ballRadius);
      if (t != null && t < tHit) {
        tHit = t;
        hitCenter = ob.pos;
        hitRadius = ob.radius;
        hitColor = ob.color ?? 0xffffff;
      }
    }

    // ray vs the cushion box (centre travel limited to +-(half - R))
    const bx = halfW - ballRadius;
    const by = halfL - ballRadius;
    const tWall = rayBox(cueBall, dir, bx, by);
    let end: THREE.Vector3;
    if (hitCenter && tHit <= tWall) {
      end = cueBall.clone().addScaledVector(dir, tHit);
      this.ghost.visible = true;
      this.ghost.position.copy(end).setZ(0.001);
      this.overlap = computeOverlap(cueBall, dir, hitCenter, ballRadius, hitRadius, hitColor);
    } else {
      end = cueBall.clone().addScaledVector(dir, Math.max(0.05, tWall));
      this.ghost.visible = false;
      this.overlap = null;
    }

    const start = cueBall.clone().setZ(ballRadius * 0.6);
    end.setZ(ballRadius * 0.6);
    (this.line.geometry as LineGeometry).setPositions([start.x, start.y, start.z, end.x, end.y, end.z]);
    this.line.computeLineDistances();
  }
}

/** Cut geometry: how much the cue ball overlaps the object ball along the aim line.
 * `p` is the perpendicular distance from the object-ball centre to the aim ray; at
 * contact it ranges 0 (dead-centre, full hit) to R+r (a grazing edge). */
function computeOverlap(
  cue: THREE.Vector3,
  dir: THREE.Vector3,
  center: THREE.Vector3,
  cueR: number,
  objR: number,
  color: number
): AimOverlap {
  const wx = center.x - cue.x;
  const wy = center.y - cue.y;
  const along = wx * dir.x + wy * dir.y; // component along the aim
  const px = wx - dir.x * along; // perpendicular component
  const py = wy - dir.y * along;
  const p = Math.hypot(px, py);
  const sumR = cueR + objR;
  const fraction = Math.max(0, Math.min(1, 1 - p / sumR));
  const crossZ = dir.x * wy - dir.y * wx; // >0: object ball left of travel
  const side = (crossZ > 0 ? -1 : 1) * (p / cueR);
  return { fraction, side, color };
}

/** Smallest positive ray-circle intersection distance (or null). */
function rayCircle(o: THREE.Vector3, d: THREE.Vector3, c: THREE.Vector3, r: number): number | null {
  const ox = o.x - c.x;
  const oy = o.y - c.y;
  const b = ox * d.x + oy * d.y;
  const cc = ox * ox + oy * oy - r * r;
  const disc = b * b - cc;
  if (disc < 0) return null;
  const t = -b - Math.sqrt(disc);
  return t > 1e-4 ? t : null;
}

/** Distance to the axis-aligned box walls |x|<=bx, |y|<=by from inside. */
function rayBox(o: THREE.Vector3, d: THREE.Vector3, bx: number, by: number): number {
  let t = Infinity;
  if (d.x > 1e-6) t = Math.min(t, (bx - o.x) / d.x);
  else if (d.x < -1e-6) t = Math.min(t, (-bx - o.x) / d.x);
  if (d.y > 1e-6) t = Math.min(t, (by - o.y) / d.y);
  else if (d.y < -1e-6) t = Math.min(t, (-by - o.y) / d.y);
  return Number.isFinite(t) ? t : 1;
}
